import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Card,
  Spin,
  Row,
  Col,
  Divider,
  Typography,
  Switch,
  InputNumber,
  App,
  Space,
  Tabs,
  Alert,
  Select,
} from 'antd';
import {
  SaveOutlined,
  ReloadOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useSystemSettings, useUpdateSystemSetting } from '@/hooks/useSettings';
import settingsService from '@/services/settingsService';

const { Text } = Typography;

/** DB category for SMTP: newer seeds use `notification`; legacy module uses `email`. */
function isOutboundSmtpCategory(category: string) {
  return category === 'notification' || category === 'email';
}

function getSettingType(setting: { setting_type?: string; data_type?: string }) {
  return setting.setting_type || setting.data_type || 'string';
}

export const SystemSettingsPage: React.FC = () => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const { data: settings, isLoading, refetch } = useSystemSettings();
  const updateSetting = useUpdateSystemSetting();
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [smtpPreset, setSmtpPreset] = useState<'custom' | 'm365' | 'gmail'>('custom');
  const [testTo, setTestTo] = useState('');
  const [testSending, setTestSending] = useState(false);

  const settingsByCategory = React.useMemo(() => {
    if (!settings) return {};
    return settings.reduce((acc, setting) => {
      const category = setting.category || 'other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(setting);
      return acc;
    }, {} as Record<string, typeof settings>);
  }, [settings]);

  // Initialize form when settings load
  useEffect(() => {
    if (settings && settings.length > 0) {
      const initialValues: any = {};
      settings.forEach((setting) => {
        const settingType = getSettingType(setting);
        
        if (settingType === 'boolean') {
          initialValues[setting.setting_key] =
            setting.setting_value === 'true' || setting.setting_value === '1';
        } else if (settingType === 'number') {
          const numValue = Number(setting.setting_value);
          initialValues[setting.setting_key] = isNaN(numValue) ? 0 : numValue;
        } else if (settingType === 'json') {
          try {
            initialValues[setting.setting_key] = JSON.parse(setting.setting_value || '[]');
          } catch {
            initialValues[setting.setting_key] = setting.setting_value;
          }
        } else {
          initialValues[setting.setting_key] = setting.setting_value || '';
        }
      });
      form.setFieldsValue(initialValues);
      setHasChanges(false); // Reset changes flag when form is initialized
    }
  }, [settings, form]);

  const handleSave = async (values: any) => {
    setSaving(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      const errors: Array<{ key: string; message: string }> = [];

      for (const [key, value] of Object.entries(values)) {
        try {
          const setting = settings?.find((s) => s.setting_key === key);
          const settingType = setting ? getSettingType(setting) : 'string';

          if (key === 'smtp_password') {
            if (value === undefined || value === null || String(value).trim() === '') {
              continue;
            }
          }

          let stringValue = String(value);

          if (settingType === 'boolean') {
            stringValue = value ? 'true' : 'false';
          }

          if (settingType === 'number') {
            const numValue = Number(value);
            if (isNaN(numValue)) {
              errors.push({ key, message: `Invalid number value: ${value}` });
              errorCount++;
              continue;
            }
            stringValue = String(numValue);
          }

          await updateSetting.mutateAsync({
            key,
            value: stringValue,
          });
          successCount++;
        } catch (error: any) {
          console.error(`Failed to update ${key}:`, error);
          const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
          errors.push({ key, message: errorMessage });
          errorCount++;
        }
      }

      if (errorCount === 0) {
        if (successCount === 0) {
          message.success('No setting updates were required (for example, SMTP password left blank keeps the existing value).');
        } else {
          message.success(`All ${successCount} settings saved successfully!`);
        }
        setHasChanges(false);
        refetch();
      } else {
        const errorDetails = errors.map(e => `${e.key}: ${e.message}`).join('; ');
        message.warning(`${successCount} settings saved, ${errorCount} failed. ${errorDetails}`);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save settings';
      message.error(`Failed to save settings: ${errorMessage}`);
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    refetch();
    setHasChanges(false);
    message.info('Settings reset to current values');
  };

  const renderInput = (setting: any) => {
    const settingType = getSettingType(setting);

    switch (settingType) {
      case 'boolean':
        return <Switch />;
      case 'number':
        return <InputNumber style={{ width: '100%' }} min={0} />;
      case 'json':
        return (
          <Input.TextArea rows={3} placeholder="Enter JSON array or object" />
        );
      default:
        if (setting.setting_key === 'smtp_password') {
          return (
            <Input.Password
              placeholder="Leave blank to keep the current password"
              autoComplete="new-password"
            />
          );
        }
        return <Input />;
    }
  };

  const handleTestEmail = async () => {
    const to = testTo.trim();
    if (!to) {
      message.warning('Enter a recipient email address');
      return;
    }
    setTestSending(true);
    try {
      await settingsService.testSystemEmail(to);
      message.success('Test email sent. Check the inbox (and spam folder).');
    } catch (e: any) {
      const msg =
        e.response?.data?.message ||
        e.response?.data?.error ||
        e.message ||
        'Failed to send test email';
      message.error(msg);
    } finally {
      setTestSending(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <Spin size="large" tip="Loading settings..." />
        </div>
      </Card>
    );
  }

  if (!settings || settings.length === 0) {
    return (
      <Card>
        <Alert
          message="No Settings Found"
          description="No settings are currently configured in the database. Please contact your administrator."
          type="warning"
          showIcon
          action={
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      </Card>
    );
  }

  const hasEmailCategoryTab = Boolean(
    settingsByCategory.email && settingsByCategory.email.length > 0
  );

  const categoryNames: Record<string, string> = {
    company: 'Company Information',
    system: 'System Configuration',
    quality: 'Quality Management',
    notification: 'Notifications',
    email: 'Email',
    document: 'Document Management',
    personnel: 'Personnel & Competency',
    calibration: 'Calibration',
    backup: 'Backup',
    general: 'General',
    security: 'Security',
    ratelimit: 'Rate limit',
    inventory: 'Inventory',
  };

  const tabItems = Object.entries(settingsByCategory).map(([category, categorySettings]) => {
    const showSmtpHelpTestPreset =
      isOutboundSmtpCategory(category) && (category === 'email' || !hasEmailCategoryTab);

    return {
    key: category,
    label: categoryNames[category] || category.toUpperCase(),
    children: (
      <Row gutter={[24, 16]}>
        {showSmtpHelpTestPreset && (
          <>
            <Col span={24}>
              <Alert
                type="info"
                showIcon
                message="Office 365, Gmail, and shared mailboxes"
                description={
                  <div>
                    <p>
                      <strong>Microsoft 365:</strong> Use <code>smtp.office365.com</code>, port <code>587</code>, with
                      the <strong>Use SSL/TLS</strong> switch on (STARTTLS). Sign in with a <strong>licensed user</strong>
                      ; for shared mailboxes, that user needs <strong>Send As</strong> / <strong>Send on behalf</strong>,
                      and <strong>Sender email</strong> can be the shared address.
                    </p>
                    <p>
                      <strong>If the test fails with 535 / &quot;SmtpClientAuthentication is disabled&quot;:</strong>{' '}
                      your tenant has <strong>SMTP AUTH</strong> turned off. A Microsoft 365 admin must enable it for
                      the mailbox (or whole org) — this app cannot bypass that. Follow{' '}
                      <a
                        href="https://aka.ms/smtp_auth_disabled"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        https://aka.ms/smtp_auth_disabled
                      </a>
                      .
                    </p>
                    <p>
                      <strong>Google Workspace / Gmail:</strong> Often <code>smtp.gmail.com</code> port <code>587</code>.
                      You may need an app password or a dedicated sending account, depending on admin policy. Shared or
                      group &quot;send as&quot; follows the same idea: sign in as the allowed user, set From to the
                      permitted address.
                    </p>
                    <p>
                      <strong>Database vs environment:</strong> When email is enabled here and SMTP host is set, these
                      values override <code>SMTP_*</code> environment variables. Otherwise the server falls back to env
                      configuration.
                    </p>
                  </div>
                }
              />
            </Col>
            <Col span={24}>
              <Card size="small" title="Send test email">
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Sends a short message using the current configuration (saved Email / Notification SMTP settings if
                    email is enabled and host is set, otherwise <code>SMTP_*</code> from the server environment). If you
                    change SMTP fields below, click <strong>Save Changes</strong> before testing so the server picks
                    them up.
                  </Text>
                  <Space wrap>
                    <Input
                      style={{ minWidth: 280 }}
                      placeholder="Recipient email"
                      value={testTo}
                      onChange={(e) => setTestTo(e.target.value)}
                      onPressEnter={() => void handleTestEmail()}
                      type="email"
                    />
                    <Button
                      type="primary"
                      icon={<MailOutlined />}
                      loading={testSending}
                      onClick={() => void handleTestEmail()}
                    >
                      Send test
                    </Button>
                  </Space>
                </Space>
              </Card>
            </Col>
            <Col span={24}>
              <Card size="small" title="Provider preset">
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text type="secondary">
                    Fills suggested host, port, and TLS for common providers. You can still edit fields afterward.
                  </Text>
                  <Select
                    value={smtpPreset}
                    onChange={(v: 'custom' | 'm365' | 'gmail') => {
                      setSmtpPreset(v);
                      setHasChanges(true);
                      if (v === 'm365') {
                        const base = { smtp_host: 'smtp.office365.com', smtp_port: 587 };
                        form.setFieldsValue(
                          category === 'email'
                            ? { ...base, smtp_use_ssl: false }
                            : { ...base, smtp_secure: false }
                        );
                      } else if (v === 'gmail') {
                        const base = { smtp_host: 'smtp.gmail.com', smtp_port: 587 };
                        form.setFieldsValue(
                          category === 'email'
                            ? { ...base, smtp_use_ssl: false }
                            : { ...base, smtp_secure: false }
                        );
                      }
                    }}
                    style={{ width: 320 }}
                    options={[
                      { value: 'custom', label: 'Custom' },
                      { value: 'm365', label: 'Microsoft 365 (suggested)' },
                      { value: 'gmail', label: 'Google / Gmail (suggested)' },
                    ]}
                  />
                </Space>
              </Card>
            </Col>
          </>
        )}
        {categorySettings.map((setting) => {
          const st = getSettingType(setting);
          return (
            <Col xs={24} lg={12} key={setting.setting_key}>
              <Card
                size="small"
                style={{
                  height: '100%',
                  borderLeft: `3px solid ${
                    category === 'company' ? '#145A54' : category === 'quality' ? '#52c41a' : '#faad14'
                  }`,
                }}
              >
                <Form.Item
                  label={
                    <Space>
                      <Text strong>
                        {setting.setting_key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      </Text>
                    </Space>
                  }
                  name={setting.setting_key}
                  valuePropName={st === 'boolean' ? 'checked' : 'value'}
                  help={<Text type="secondary" style={{ fontSize: '12px' }}>{setting.description}</Text>}
                >
                  {renderInput(setting)}
                </Form.Item>
              </Card>
            </Col>
          );
        })}
      </Row>
    ),
  };
  });

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <SettingOutlined />
            <span>System Settings</span>
          </Space>
        }
        extra={
          <Space>
            <Text type="secondary">
              {settings.length} settings configured
            </Text>
          </Space>
        }
      >
        <Form 
          form={form} 
          layout="vertical" 
          onFinish={handleSave}
          onValuesChange={() => setHasChanges(true)}
        >
          <Tabs items={tabItems} />

          <Divider />

          <Space size="middle">
            <Button 
              type="primary" 
              icon={<SaveOutlined />} 
              htmlType="submit" 
              loading={saving}
              disabled={!hasChanges || saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={handleReset}
              disabled={!hasChanges || saving}
            >
              Reset
            </Button>
            {hasChanges && !saving && (
              <Text type="warning">
                <CheckCircleOutlined /> You have unsaved changes
              </Text>
            )}
            {saving && (
              <Text type="secondary">
                Saving settings...
              </Text>
            )}
          </Space>
        </Form>
      </Card>
    </div>
  );
};

